import type { InkStroke, InkTool } from '../model/schema.js';
import { INK_TOOLS } from '../model/schema.js';

/** Pressure sensitivity per device (decision J6): light = little force for full width. */
export const PRESSURE_CURVES = ['light', 'normal', 'firm'] as const;
export type PressureCurve = (typeof PRESSURE_CURVES)[number];

/** Exponent applied to the raw pressure (p^γ). */
export const PRESSURE_GAMMA: Readonly<Record<PressureCurve, number>> = {
  light: 0.6,
  normal: 1,
  firm: 1.6,
};

export const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

export function applyPressureCurve(p: number, curve: PressureCurve): number {
  return clamp01(p) ** PRESSURE_GAMMA[curve];
}

/** Mouse pressure from drawing speed (fast = thin), smoothed against the previous value. */
export function simulatedPressure(prev: number, distancePx: number, dtMs: number): number {
  const speed = distancePx / Math.max(1, dtMs);
  const target = 0.3 + 0.6 * Math.exp(-speed / 1.5);
  return prev + (target - prev) * 0.35;
}

/**
 * perfect-freehand options as plain data (web passes them to `getStroke`). Tapers are given in
 * multiples of the stroke size (perfect-freehand expects absolute lengths: multiply by `size`).
 */
export interface FreehandOptions {
  thinning: number;
  streamline: number;
  smoothing: number;
  start: { cap: boolean; taper: number };
  end: { cap: boolean; taper: number };
}

export interface ToolPreset {
  tool: InkTool;
  freehand: FreehandOptions;
  /** Base opacity (0–1). */
  opacity: number;
  /** Pencil: opacity follows the average pressure. */
  pressureOpacity: boolean;
  /** Canvas2D / SVG blend mode. */
  blend: 'normal' | 'multiply';
  /** Render group: highlighter strokes go below all other ink (decision I4/J2). */
  group: 'highlight' | 'ink';
  /** Pencil grain texture. */
  grain: boolean;
  /** Width presets in screen pixels at the zoom level where the stroke is drawn. */
  widthsPx: readonly [number, number, number];
  /** Slider range in screen pixels. */
  minPx: number;
  maxPx: number;
}

const round = { cap: true, taper: 0 };

export const TOOL_PRESETS: Readonly<Record<InkTool, ToolPreset>> = {
  pen: {
    tool: 'pen',
    freehand: { thinning: 0.6, streamline: 0.5, smoothing: 0.5, start: round, end: round },
    opacity: 1,
    pressureOpacity: false,
    blend: 'normal',
    group: 'ink',
    grain: false,
    widthsPx: [2, 4, 8],
    minPx: 1,
    maxPx: 32,
  },
  pencil: {
    tool: 'pencil',
    freehand: {
      thinning: 0.45,
      streamline: 0.35,
      smoothing: 0.6,
      start: { cap: true, taper: 2 },
      end: { cap: true, taper: 2 },
    },
    opacity: 0.9,
    pressureOpacity: true,
    blend: 'normal',
    group: 'ink',
    grain: true,
    widthsPx: [2, 4, 8],
    minPx: 1,
    maxPx: 32,
  },
  fineliner: {
    tool: 'fineliner',
    freehand: { thinning: 0, streamline: 0.5, smoothing: 0.5, start: round, end: round },
    opacity: 1,
    pressureOpacity: false,
    blend: 'normal',
    group: 'ink',
    grain: false,
    widthsPx: [1, 2, 4],
    minPx: 0.5,
    maxPx: 16,
  },
  highlighter: {
    tool: 'highlighter',
    freehand: {
      thinning: 0,
      streamline: 0.6,
      smoothing: 0.6,
      start: { cap: false, taper: 0 },
      end: { cap: false, taper: 0 },
    },
    opacity: 0.35,
    pressureOpacity: false,
    blend: 'multiply',
    group: 'highlight',
    grain: false,
    widthsPx: [12, 20, 32],
    minPx: 4,
    maxPx: 64,
  },
};

export function isInkTool(v: unknown): v is InkTool {
  return (INK_TOOLS as readonly unknown[]).includes(v);
}

/** Resolved rendering parameters of a committed (or live) stroke. */
export interface StrokeStyle {
  /** Outline diameter in the stroke's coordinate units (metres). */
  size: number;
  opacity: number;
  freehand: FreehandOptions;
  blend: ToolPreset['blend'];
  group: ToolPreset['group'];
  grain: boolean;
}

/** Maximum widening of a pencil held flat (tilt 90°). */
export const PENCIL_TILT_WIDEN = 1.2;
/** Maximum opacity loss of a pencil held flat. */
export const PENCIL_TILT_SOFTEN = 0.45;

/**
 * Style of a stroke from its tool preset. Pencil: opacity follows the average pressure and tilt
 * widens / softens the line (decision I4).
 */
export function strokeStyle(
  stroke: Pick<InkStroke, 'tool' | 'width' | 'tilt'>,
  avgPressure = 0.5,
): StrokeStyle {
  const preset = TOOL_PRESETS[stroke.tool];
  const tilt = (stroke.tilt ?? 0) / 90;
  let size = stroke.width;
  let opacity = preset.opacity;
  if (preset.pressureOpacity) opacity *= 0.4 + 0.6 * clamp01(avgPressure);
  if (stroke.tool === 'pencil') {
    size *= 1 + PENCIL_TILT_WIDEN * tilt;
    opacity *= 1 - PENCIL_TILT_SOFTEN * tilt;
  }
  return {
    size,
    opacity,
    freehand: preset.freehand,
    blend: preset.blend,
    group: preset.group,
    grain: preset.grain,
  };
}

/** Width in metres for a screen-pixel width at the current zoom (px per metre). */
export function widthFromPx(px: number, scale: number): number {
  return px / scale;
}

/** Next / previous width step (`[` / `]` keys), clamped to the tool's slider range. */
export function stepWidth(tool: InkTool, px: number, dir: 1 | -1): number {
  const { minPx, maxPx } = TOOL_PRESETS[tool];
  const next = dir === 1 ? px * 1.25 : px / 1.25;
  return Math.round(Math.min(maxPx, Math.max(minPx, next)) * 10) / 10;
}

/** Average tilt angle (degrees from upright) from Pointer Events tiltX/tiltY (degrees). */
export function tiltFromPointer(tiltX: number, tiltY: number): number {
  const tx = Math.tan((tiltX * Math.PI) / 180);
  const ty = Math.tan((tiltY * Math.PI) / 180);
  return (Math.atan(Math.hypot(tx, ty)) * 180) / Math.PI;
}

/** Tilt from altitudeAngle (radians, π/2 = upright; Safari 18.2+). */
export function tiltFromAltitude(altitude: number): number {
  return Math.min(90, Math.max(0, 90 - (altitude * 180) / Math.PI));
}
